import { useEffect, useRef, useState, type ReactNode } from "react";
import type { DailyPoint } from "../api";

/** A labelled number. `hint` carries the caveat that keeps the number honest. */
export function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="tile">
      <div className="tile-label">{label}</div>
      <div className="tile-value">{value}</div>
      {hint ? <div className="tile-hint">{hint}</div> : null}
    </div>
  );
}

export function Banner({
  tone = "warn",
  children,
}: {
  tone?: "warn" | "error" | "good";
  children: ReactNode;
}) {
  return (
    <div className={`banner ${tone}`} role={tone === "error" ? "alert" : "status"}>
      {children}
    </div>
  );
}

/**
 * A read-only value with a copy button — the control the partner cabinet is
 * really built around, since "what's my link" is the question it exists to
 * answer.
 *
 * Falls back to a hidden textarea and `execCommand("copy")` when the
 * clipboard API is unavailable, which is the case on any non-HTTPS origin —
 * including the `localhost` a developer will inevitably test this on.
 */
export function CopyField({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Without this, unmounting while the "Copied" state is pending sets state
  // on a dead component every time a partner closes the view quickly.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  async function copy() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const scratch = document.createElement("textarea");
        scratch.value = value;
        scratch.setAttribute("readonly", "");
        scratch.style.position = "absolute";
        scratch.style.left = "-9999px";
        document.body.appendChild(scratch);
        scratch.select();
        document.execCommand("copy");
        document.body.removeChild(scratch);
      }
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard access can be refused outright. Selecting the text is
      // still possible by hand, so say nothing and let them.
    }
  }

  return (
    <div>
      {label ? <div className="tile-label" style={{ marginBottom: 6 }}>{label}</div> : null}
      <div className="copy">
        <code>{value}</code>
        <button type="button" onClick={copy} aria-live="polite">
          {copied ? "Copied ✓" : "Copy"}
        </button>
      </div>
    </div>
  );
}

/**
 * Two overlaid bar series — visitors behind, leads in front — across the
 * cabinet's 30-day window.
 *
 * Days with no activity come back absent from the API rather than as zeroes,
 * and are drawn as a gap rather than a zero-height bar: "nobody came" and
 * "we have no data" should not look the same. Hand-rolled SVG because a chart
 * library would be the single largest thing in this bundle, for one chart.
 */
export function Sparkline({
  days,
  visitors,
  leads,
}: {
  days: number;
  visitors: DailyPoint[];
  leads: DailyPoint[];
}) {
  const byDay = (points: DailyPoint[]) =>
    Object.fromEntries(points.map((p) => [p.day, p.count]));
  const visitorsByDay = byDay(visitors);
  const leadsByDay = byDay(leads);

  const today = new Date();
  const axis = Array.from({ length: days }, (_, i) => {
    const d = new Date(today.getTime() - (days - 1 - i) * 86_400_000);
    return d.toISOString().slice(0, 10);
  });

  const peak = Math.max(1, ...axis.map((day) => visitorsByDay[day] ?? 0));
  const slot = 100 / days;
  const barWidth = Math.max(slot * 0.62, 0.6);

  if (visitors.length === 0 && leads.length === 0) {
    return <p className="dim tiny">No activity yet — the chart fills in as people open your link.</p>;
  }

  return (
    <svg
      className="spark"
      viewBox="0 0 100 32"
      preserveAspectRatio="none"
      role="img"
      aria-label={`Daily visitors and leads over the last ${days} days`}
    >
      {axis.map((day, i) => {
        const v = visitorsByDay[day];
        const l = leadsByDay[day];
        const x = i * slot + (slot - barWidth) / 2;
        return (
          <g key={day}>
            {v ? (
              <rect x={x} y={32 - (v / peak) * 32} width={barWidth} height={(v / peak) * 32}>
                <title>{`${day}: ${v} visitor${v === 1 ? "" : "s"}`}</title>
              </rect>
            ) : null}
            {l ? (
              <rect
                className="lead"
                x={x}
                y={32 - Math.max((l / peak) * 32, 2)}
                width={barWidth}
                height={Math.max((l / peak) * 32, 2)}
              >
                <title>{`${day}: ${l} lead${l === 1 ? "" : "s"}`}</title>
              </rect>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}
